/* eslint-disable no-unused-vars */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import closeSvg from 'src/assets/close.svg';
import { CommonInput, FormErrorWrap, RegularButton, FullScreenLoading, AlertAdapter } from 'pg-ui-library';
import ReactModal from 'react-modal';
import { useFormik } from 'formik';
import * as yup from 'yup';
import PersonalInfoService from 'src/services/personal-info.service';
import { CloseIcon, SecurityForm, FormInputWrapper, ActionWrapper, SecurityHeading } from './styles';
import { useUpdateSecurityAnswer } from '../../mutations/update-security-ques.mutation';

const SecurityQuesForm: React.FC<{ username: string; onSuccess: () => void }> = ({ username, onSuccess }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const { data: ques, isLoading: isLoadingQuestions } = useQuery(['security-question-ans'], async () => {
    const result = await PersonalInfoService.getSecurityQues(username);
    return result;
  });

  const updateSecurityAnswer = useUpdateSecurityAnswer();
  const validationSchema = yup.object().shape({
    answers: yup.array().of(yup.string().required('Answer is required')),
  });

  const formik = useFormik({
    initialValues: {
      answers: ques ? ques.map(() => '') : [],
    },
    validationSchema,
    onSubmit: async values => {
      if (ques && ques.length > 0) {
        const formattedValues = ques.map((question, index) => ({
          userQuestionAnswerId: question.userQuestionAnswerId,
          answer: values.answers[index],
        }));
        await updateSecurityAnswer.mutateAsync(formattedValues);
        onSuccess();
      } else {
        AlertAdapter('Questions data is not available or incomplete', 'error');
      }
    },
  });

  useEffect(() => {
    if (ques) {
      formik.setValues({
        answers: ques.map(question => question.answer || ''),
      });
    }
  }, [ques]);

  return (
    <>
      <SecurityForm onSubmit={formik.handleSubmit}>
        {(isLoadingQuestions || updateSecurityAnswer.isLoading) && <FullScreenLoading />}
        <SecurityHeading>Security Question</SecurityHeading>
        {ques?.map((question, index) => (
          <FormInputWrapper key={question.id}>
            <CommonInput
              id={`ques-${index}`}
              name={`answers.${index}`}
              label={`${index + 1} - ${question.question}`}
              placeholder="Answer"
              value={formik.values.answers[index]}
              onChange={event => formik.setFieldValue(`answers.${index}`, event.target.value)}
              style={{ width: '100%' }}
              disabled={!isEditing}
            />
            <FormErrorWrap
              errorMessage={formik.errors.answers?.[index] as string}
              visible={!!formik.errors.answers?.[index]}
            />
          </FormInputWrapper>
        ))}
      </SecurityForm>
      <ActionWrapper>
        <RegularButton
          type="button"
          onClick={() => {
            if (isEditing) {
              formik.handleSubmit();
            } else {
              setIsEditing(true);
            }
          }}
          disabled={updateSecurityAnswer.isLoading}
        >
          {isEditing ? t('global.form.update') : t('global.form.edit')}
        </RegularButton>
      </ActionWrapper>
    </>
  );
};

export const SecurityQuesModal: React.FC<{
  username: string;
  isOpen: boolean;
  setIsOpen: Function;
}> = ({ username, isOpen, setIsOpen }) => {
  const modalRef = useRef(null);

  const handleModalClose = () => {
    setIsOpen(false);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: 'max-content',
          minWidth: '550px',
          maxWidth: '720px',
          height: 'max-content',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        },
      }}
      onRequestClose={handleModalClose}
      // eslint-disable-next-line no-return-assign
      contentRef={(node: any) => (modalRef.current = node)}
    >
      <CloseIcon src={closeSvg} onClick={handleModalClose} />
      <SecurityQuesForm username={username} onSuccess={handleModalClose} />
    </ReactModal>
  );
};
